import { Box, Skeleton, Text } from '@kluein/klue-ui';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { DAYS_TO_FREQUENCY } from 'api/types/card-schedule.types';
import {
  CARD_CONTENT_ALLOWED_TAGS,
  CARD_CONTENT_ALLOWED_ATTR,
} from 'klue-html/constants';
import sanitize from 'klue-html/sanitizer';
import TEST_IDS from 'test-ids';

import DynamicBlock from '../../blocks/dynamicBlock/DynamicBlock';
import { transformHTML } from '../../transformers';

import type { UseCardInteractionLogType } from '../../hooks/use-card-interaction-log/useCardInteractionLog';
import type { CardDataType } from 'api/api.types';
import type { TextJson, Formula } from 'klue-html/formulas/formulas.types';
import type { LogEventType } from 'lib/analytics';

type KlueCardContentProps = {
  hasLoaded: boolean;
  isCardViewerContent?: boolean;
  cardData?: CardDataType;
  initialHeight?: string | number;
  cardId?: number;
  logEventLabel: string;
  rank?: number | null;
  logEvent: (data: LogEventType) => void;
  logCardInteraction: UseCardInteractionLogType;
  KLUE_CARD_CUSTOM_MARGIN_CONTENT: string;
  KLUE_CARD_INTERNAL_GUTTER: number;
  showAiProcessingPlaceholder?: boolean;
  aiProcessingFrequencyDays?: number | null;
};

const ContentRenderer = React.memo<{
  textJson: TextJson[];
  logEventLabel: string;
  cardId?: number;
}>(({ textJson, logEventLabel, cardId }) => {
  const renderContentByType = React.useCallback(
    ({ block, blockKey }: { block: TextJson; blockKey: string }) => {
      const renderers = {
        html: () => {
          const sanitized = sanitize(block?.data as string, {
            ALLOWED_TAGS: CARD_CONTENT_ALLOWED_TAGS,
            ADD_ATTR: CARD_CONTENT_ALLOWED_ATTR,
          });

          return (
            <div key={blockKey}>
              {transformHTML(sanitized, logEventLabel, cardId)}
            </div>
          );
        },
        formula: () => (
          <DynamicBlock
            key={blockKey}
            formula={block?.data as Formula}
            cardId={cardId}
          />
        ),
      };

      return renderers;
    },
    [cardId, logEventLabel],
  );

  return (
    <>
      {textJson?.map((block, i: number) => {
        const blockKey = `${block.type}_${i}`;
        return renderContentByType({
          block,
          blockKey,
        })[block.type]();
      })}
    </>
  );
});

ContentRenderer.displayName = 'ContentRenderer';

export const KlueCardContent = React.memo<KlueCardContentProps>(
  ({
    hasLoaded,
    isCardViewerContent,
    cardData,
    initialHeight = 'auto',
    cardId,
    logEventLabel,
    KLUE_CARD_CUSTOM_MARGIN_CONTENT,
    KLUE_CARD_INTERNAL_GUTTER,
    showAiProcessingPlaceholder,
    aiProcessingFrequencyDays,
  }) => {
    const { t } = useTranslation(['Card']);
    // Resolve the frequencyDays integer to the human label so
    // i18next's `context` interpolation picks up
    // `scheduledPlaceholder.line2_<frequency>`; a null / unknown value
    // falls through to the generic base key.
    const aiProcessingFrequencyLabel =
      aiProcessingFrequencyDays != null
        ? DAYS_TO_FREQUENCY[aiProcessingFrequencyDays]
        : undefined;
    const contentStyle = React.useMemo(
      () => ({
        margin: isCardViewerContent
          ? KLUE_CARD_CUSTOM_MARGIN_CONTENT
          : `${KLUE_CARD_INTERNAL_GUTTER}px`,
      }),
      [
        isCardViewerContent,
        KLUE_CARD_CUSTOM_MARGIN_CONTENT,
        KLUE_CARD_INTERNAL_GUTTER,
      ],
    );

    const skeletonBoxStyle = React.useMemo(
      () => ({
        min: String(initialHeight),
      }),
      [initialHeight],
    );

    if (!hasLoaded) {
      return (
        <div style={{ flexGrow: 1, flexShrink: 0 }}>
          <Box margin="large" height={skeletonBoxStyle}>
            <Skeleton height={'200px'} animated={true} />
          </Box>
        </div>
      );
    }

    if (!cardData) {
      return null;
    }

    if (showAiProcessingPlaceholder) {
      return (
        <div style={{ flexGrow: 1, flexShrink: 0 }}>
          <Box margin="large">
            <Text
              color="var(--mantine-color-dimmed, #868e96)"
              style={{ fontStyle: 'italic', marginBottom: '4px' }}
            >
              {t('Card:scheduledPlaceholder.line1')}
            </Text>
            <Text
              color="var(--mantine-color-dimmed, #868e96)"
              style={{ fontStyle: 'italic' }}
            >
              {t('Card:scheduledPlaceholder.line2', {
                context: aiProcessingFrequencyLabel,
              })}
            </Text>
          </Box>
        </div>
      );
    }

    return (
      <div style={{ flexGrow: 1, flexShrink: 0 }}>
        <div
          data-test-id={TEST_IDS.klueCard.content}
          className="chromatic-ignore card-static-html_body"
          style={contentStyle}
        >
          <ContentRenderer
            textJson={
              Array.isArray(cardData?.textJson) ? cardData.textJson : []
            }
            logEventLabel={logEventLabel}
            cardId={cardId}
          />
        </div>
      </div>
    );
  },
);

KlueCardContent.displayName = 'KlueCardContent';

export default KlueCardContent;
